import { AbsgArrayHelper } from '@absg/angular-common';
import { Constants, DispenseTypes, InventoryItemUnderHood, PickListItem } from '@absg/nim-client-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { PaginationInstance } from 'ngx-pagination';
import { BehaviorSubject } from 'rxjs';
import { PaginationControlsComponent } from 'shared.components/pagination-controls/pagination-controls.component';
import { IPageablePickListComponent } from 'shared.models/pageablePickList.interface';
import { StationListTypes } from 'shared.root/enums/stationlist-type.enum';
import { SubSink } from 'subsink';

@Component({
  selector: '[nc-pageable-pick-list]',
  templateUrl: './pageable-pick-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PageablePickListComponent implements OnInit, OnDestroy, IPageablePickListComponent {
  @Input() isModalOpen = new BehaviorSubject<boolean>(false);
  @Input() isSelectedItem: PickListItem;
  @Input() title: string;
  @Input() items: PickListItem[];
  @Input() name: string;
  @Input() dynamicIdSuffix: string;
  @Input() isPagination = false;
  @Input() warningMessage: string;
  @Input() warningTitle: string;
  /* Made displayWitnessAlert optional so that other places are not impacted.
     Explicit value set is required when witness alert is needed.*/
  @Input() displayWitnessAlert = new BehaviorSubject<InventoryItemUnderHood[]>([]);
  @Output() itemClicked = new EventEmitter<PickListItem>();
  @Output() clearSelection = new EventEmitter();
  @ViewChild('searchFieldInput') searchTextFieldInput: ElementRef;

  pagingConfig: PaginationInstance = PaginationControlsComponent.getDefaultConfig();

  itemDescription: string;
  stationListSetting = StationListTypes.DispenseType;
  isUnderHoodModalOpen = new BehaviorSubject<boolean>(false);
  stations = new BehaviorSubject<InventoryItemUnderHood[]>([]);
  filteredItems: InventoryItemUnderHood[] = [];
  isOpen: boolean;
  private sub = new SubSink();

  constructor(private cd: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.pagingConfig.itemsPerPage = 12;
    const name = !this.name ? 'pickListPaging' : this.name;
    this.pagingConfig.id = name;
    this.sub.sink = this.isModalOpen.subscribe((value: boolean) => {
      if (value) {
        this.pagingConfig.currentPage = this.getPageOfSelectedClinic();
        if (this.displayWitnessAlert?.getValue().length > 0) {
          const itemList = this.displayWitnessAlert.getValue();
          this.filteredItems = AbsgArrayHelper.filter(itemList, x => x.isWitness === Constants.onText);
        }
      }
      this.isOpen = value;
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onCancelPickListModal(): void {
    this.closeModal();
  }

  onItemClicked(item: PickListItem): void {
    this.itemClicked.emit(item);
    this.isSelectedItem = item;
    this.closeModal();
  }

  onClearPickListModal(): void {
    this.clearSelection.emit(null);
    this.isSelectedItem = null;
    this.closeModal();
  }

  isSelected(item: PickListItem): boolean {
    if (item && this.isSelectedItem) {
      return item.text === this.isSelectedItem.text
        && item.id === this.isSelectedItem.id;
    }
    return false;
  }

  clearSearch(): void {
    this.itemDescription = '';
    this.searchTextFieldInput.nativeElement.value = '';
    this.searchTextFieldInput.nativeElement.focus();
  }

  getPaginationAndSearch(): boolean {
    if (!!this.items && this.items.length > this.pagingConfig.itemsPerPage) {
      return true;
    }
    return this.isPagination;
  }

  onSearchFilterChanged(): void {
    this.pagingConfig.currentPage = 1;
  }

  filterPickListItems(): PickListItem[] {
    if (this.itemDescription) {
      return AbsgArrayHelper.filter(this.items, (item: PickListItem) => {
        const text = item.text.toLowerCase();
        const itemDesc = this.itemDescription.toLowerCase();
        return text.includes(itemDesc) || text === itemDesc;
      });
    } else {
      return this.items;
    }
  }

  getId(itemText: string): string {
    if (!itemText) { return ''; }
    const cleanupText = itemText.replace(/\s/g, '').replace(/[&/\\#,+()$~%.'":*?<>{}!@^]/g, '');

    if (this.dynamicIdSuffix && this.dynamicIdSuffix !== '') {
      return cleanupText + this.dynamicIdSuffix;
    } else {
      return `PickId_${cleanupText}`;
    }
  }

  trackItem(__: number, item: PickListItem): string {
    if (!item) { return undefined; }

    return item.text;
  }

  setFocus(event: TransitionEvent): void {
    if (!this.isModalOpen.value) {
      return; // exit if modal is closed
    }
    if (!!event && event.propertyName === 'opacity' && !!this.searchTextFieldInput) {
      this.searchTextFieldInput.nativeElement.focus();
    }
  }

  setDisable(item: PickListItem): boolean {
    if (item.isDisabled) { return true; }
    // TODO: Remove rest. This seems like a unique condition for a specific workflow and not generic.
    if (this.title === 'Dispense Type' && this.filteredItems.length > 0
      && item.id === DispenseTypes.ReturnToUnderHood) {
      return true;
    }
    return false;
  }

  showWitnessAlert(): boolean {
    return this.filteredItems.length > 0;
  }

  showWarningAlert(): boolean {
    return this.warningMessage !== '' && this.warningMessage !== null && this.warningMessage !== undefined;
  }

  showStationListModal(): void {
    this.stations.next(this.filteredItems);
    this.isUnderHoodModalOpen.next(true);
  }

  getStationButtonText(): string {
    if (this.filteredItems.length > 0) {
      return `View ${this.filteredItems.length} Station(s)`;
    } else {
      return '';
    }
  }

  private closeModal(): void {
    this.itemDescription = '';
    this.isModalOpen.next(false);
  }

  private getPageOfSelectedClinic(): number {
    const index = this.items ? this.items.findIndex(item => this.isSelected(item)) + 1 : 0;
    if (index < this.pagingConfig.itemsPerPage) {
      return 1;
    }
    return Math.ceil(index / this.pagingConfig.itemsPerPage);
  }
}
